// Design 2
import React, { useState, useEffect, useRef } from 'react';
import ColorPickerField from '../../components/ColorPickerField';
import {
    Box,
    Container,
    Grid,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Typography,
    Checkbox,
    FormControlLabel,
    ToggleButton,
    ToggleButtonGroup,
    Button,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useTheme,
    Divider,
    CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import QRCodeStyling from 'qr-code-styling';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { resizeLogoFile } from './qrLogoUtils';

const HIGHEST_ERROR_CORRECTION_LEVEL = 'H';
const HIGHEST_EXPORT_QUALITY = 4;

const DesignerTemplatesPanel = () => {
    const theme = useTheme();
    const qrCodeRef = useRef(null);
    const previewRef = useRef(null);

    // Core settings
    const [contentType, setContentType] = useState('url');
    const [contentValue, setContentValue] = useState('https://example.com');
    const [vcard, setVcard] = useState({ name: '', phone: '', email: '' });
    const [qrShape, setQrShape] = useState('square');
    const [showScanMe, setShowScanMe] = useState(true);
    const [ScanMe, setScanMe] = useState("SCAN ME");
    const [borderWidth, setBorderWidth] = useState(1);
    const [borderColor, setBorderColor] = useState('#000000');
    const [marginH, setMarginH] = useState(5);
    const [marginV, setMarginV] = useState(5);
    const [dotsColor, setDotsColor] = useState('#000000');
    const [bgColor, setBgColor] = useState('#ffffff');
    const [scanMeFontSize, setScanMeFontSize] = useState(16);
    const [scanMePosition, setScanMePosition] = useState('bottom');
    const [scanMeFontColor, setScanMeFontColor] = useState('#ffffff');
    const [scanMeBorderColor, setScanMeBorderColor] = useState('#000000');

    // Quality, Logo
    const [logoData, setLogoData] = useState(null);
    const [isQrRendering, setIsQrRendering] = useState(false);
    // const debouncedLogoMargin = useDebouncedValue(marginH, 220);

    const qrCode = useRef(
        new QRCodeStyling({
            width: 300,
            height: 300,
            data: contentValue,
            qrOptions: { typeNumber: 0, mode: 'Byte', errorCorrectionLevel: HIGHEST_ERROR_CORRECTION_LEVEL },
            dotsOptions: { type: qrShape, color: dotsColor },
            backgroundOptions: { color: bgColor },
        })
    ).current;

    useEffect(() => {
        setIsQrRendering(true);

        let paintFrameId;
        let updateFrameId;

        paintFrameId = window.requestAnimationFrame(() => {
            updateFrameId = window.requestAnimationFrame(() => {

                let data = contentValue;
                if (contentType === 'email') data = `mailto:${contentValue}`;
                if (contentType === 'phone') data = `tel:${contentValue}`;
                if (contentType === 'vcard') {
                    data = `BEGIN:VCARD\nVERSION:3.0\nFN:${vcard.name}\nTEL:${vcard.phone}\nEMAIL:${vcard.email}\nEND:VCARD`;
                }

                qrCode.update({
                    data,
                    dotsOptions: { type: qrShape, color: dotsColor },
                    backgroundOptions: { color: bgColor },
                });

                if (!qrCodeRef.current.hasChildNodes()) {
                    qrCode.append(qrCodeRef.current);
                }

                setIsQrRendering(false);
            });
        });

        return () => {
            if (paintFrameId) window.cancelAnimationFrame(paintFrameId);
            if (updateFrameId) window.cancelAnimationFrame(updateFrameId);
        };
    }, [contentType, contentValue, vcard, qrShape, dotsColor, bgColor, qrCode]);

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];

        if (!file) return;

        // Keep uploads small before QR rendering so logo changes do not block the page.
        if (file.size > 2 * 1024 * 1024) { // 2MB
            alert('Logo is too large to save between sessions. Please use smaller images.');
            e.target.value = '';
            return;
        }

        try {
            const optimizedLogo = await resizeLogoFile(file);
            setLogoData(optimizedLogo);
        } catch (logoError) {
            alert(logoError.message || 'Unable to optimize this logo. Please try a different image.');
        } finally {
            e.target.value = '';
        }
    };

    const handleDownload = async (ext) => {
        if (ext === 'svg') {
            qrCode.download({ extension: 'svg', name: 'qr-code' });
            return;
        }
        if (!previewRef.current) return;

        const scale = (window.devicePixelRatio || 2) * HIGHEST_EXPORT_QUALITY;
        const canvas = await html2canvas(previewRef.current, {
            backgroundColor: null,
            scale
        });

        if (ext === 'pdf') {
            const widthPx = previewRef.current.offsetWidth * scale;
            const heightPx = previewRef.current.offsetHeight * scale;
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({ unit: 'px', format: [widthPx, heightPx] });
            pdf.addImage(imgData, 'PNG', 0, 0, widthPx, heightPx);
            pdf.save('qr-code.pdf');
        } else {
            const mime = ext === 'jpeg' ? 'image/jpeg' : 'image/png';
            canvas.toBlob((blob) => {
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `qr-code.${ext}`;
                link.click();
            }, mime, 1);
        }
    };

    const buildQrSettings = () => ({
        contentType,
        contentValue,
        vcard,
        qrShape,
        dotsColor,
        bgColor,
        border: { width: borderWidth, color: borderColor },
        margin: { h: marginH, v: marginV },
        scanMe: {
            enabled: showScanMe,
            text: ScanMe,
            position: scanMePosition,
            fontSize: scanMeFontSize,
            fontColor: scanMeFontColor,
            borderColor: scanMeBorderColor
        },
        quality: HIGHEST_EXPORT_QUALITY,
        logoData
    });

    const handleSaveSettings = async () => {
        const settings = buildQrSettings();
        try {
            const response = await fetch('/api/qr-settings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(settings),
            });
            if (!response.ok) throw new Error(await response.text());
            console.log('Saved:', await response.json());
            alert('QR settings saved!');
        } catch (err) {
            console.error('Save failed:', err);
            alert('Failed to save settings.');
        }
    };

    return (
        <Container maxWidth="lg" sx={{ width: '100%', py: 4 }}>

            <Grid container spacing={4}>
                {/* Settings Column */}
                <Grid item lg={7} xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
                            QR Settings
                        </Typography>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Content Type</InputLabel>
                            <Select
                                value={contentType}
                                label="Content Type"
                                onChange={(e) => setContentType(e.target.value)}
                            >
                                <MenuItem value="url">URL</MenuItem>
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="email">Email</MenuItem>
                                <MenuItem value="phone">Phone</MenuItem>
                                <MenuItem value="vcard">vCard</MenuItem>
                            </Select>
                        </FormControl>

                        {contentType === 'vcard' ? (
                            <Stack spacing={2} sx={{ mb: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Name"
                                    value={vcard.name}
                                    onChange={(e) => setVcard({ ...vcard, name: e.target.value })}
                                />
                                <TextField
                                    fullWidth
                                    label="Phone"
                                    value={vcard.phone}
                                    onChange={(e) => setVcard({ ...vcard, phone: e.target.value })}
                                />
                                <TextField
                                    fullWidth
                                    label="Email"
                                    value={vcard.email}
                                    onChange={(e) => setVcard({ ...vcard, email: e.target.value })}
                                />
                            </Stack>
                        ) : (
                            <TextField
                                fullWidth
                                label={contentType.charAt(0).toUpperCase() + contentType.slice(1)}
                                value={contentValue}
                                onChange={(e) => setContentValue(e.target.value)}
                                sx={{ mb: 2 }}
                            />
                        )}

                        {/* QR Shape Accordion */}
                        <Accordion sx={{ mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>QR Shape</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <ToggleButtonGroup
                                    value={qrShape}
                                    exclusive
                                    fullWidth
                                    onChange={(e, val) => val && setQrShape(val)}

                                    sx={{
                                        [theme.breakpoints.down('sm')]: {
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }
                                    }}
                                >
                                    <ToggleButton value="square">Square</ToggleButton>
                                    <ToggleButton value="dots">Dots</ToggleButton>
                                    <ToggleButton value="rounded">Rounded</ToggleButton>
                                </ToggleButtonGroup>
                            </AccordionDetails>
                        </Accordion>

                        {/* Colors & Border Accordion */}
                        <Accordion sx={{ mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Colors & Border</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={2} sx={{ mb: 1 }}>
                                    <ColorPickerField
                                        label="Dot Color"
                                        value={dotsColor}
                                        onChange={(e) => setDotsColor(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <ColorPickerField
                                        label="Background Color"
                                        value={bgColor}
                                        onChange={(e) => setBgColor(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <ColorPickerField
                                        label="Border Color"
                                        value={borderColor}
                                        onChange={(e) => setBorderColor(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <TextField
                                        type="number"
                                        label="Border Width"
                                        value={borderWidth}
                                        onChange={(e) => setBorderWidth(Number(e.target.value))}
                                    />
                                </Stack>
                                <Stack spacing={2}>
                                    <TextField
                                        type="number"
                                        label="Margin H"
                                        value={marginH}
                                        onChange={(e) => setMarginH(Number(e.target.value))}
                                    />
                                    <TextField
                                        type="number"
                                        label="Margin V"
                                        value={marginV}
                                        onChange={(e) => setMarginV(Number(e.target.value))}
                                    />
                                </Stack>
                            </AccordionDetails>
                        </Accordion>

                        {/* Scan Me Options Accordion */}
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Scan Me Options</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={showScanMe}
                                            onChange={(e) => setShowScanMe(e.target.checked)}
                                        />
                                    }
                                    label="Show 'SCAN ME'"
                                    sx={{ mb: 2 }}
                                />
                                <Typography>Position</Typography>
                                <ToggleButtonGroup
                                    value={scanMePosition}
                                    exclusive
                                    fullWidth
                                    onChange={(e, val) => val && setScanMePosition(val)}
                                    sx={{
                                        mb: 2,
                                        [theme.breakpoints.down('sm')]: {
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }
                                    }}
                                >
                                    <ToggleButton value="top">Top</ToggleButton>
                                    <ToggleButton value="bottom">Bottom</ToggleButton>
                                </ToggleButtonGroup>

                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Font Size (px)"
                                    value={scanMeFontSize}
                                    onChange={(e) => setScanMeFontSize(Number(e.target.value))}
                                    sx={{ mb: 2 }}
                                />

                                <ColorPickerField
                                    fullWidth
                                    label="Font Color"
                                    value={scanMeFontColor}
                                    onChange={(e) => setScanMeFontColor(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ mb: 2 }}
                                />

                                <ColorPickerField
                                    fullWidth
                                    label="Border Color"
                                    value={scanMeBorderColor}
                                    onChange={(e) => setScanMeBorderColor(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ mb: 2 }}
                                />

                                <TextField
                                    fullWidth
                                    type="text"
                                    label="Replace Scan Me"
                                    value={ScanMe}
                                    onChange={(e) => setScanMe(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ mb: 2 }}
                                />
                            </AccordionDetails>
                        </Accordion>

                        {/* Logo & Quality Section */}
                        <Stack spacing={2} sx={{ mt: 2 }}>

                            <Button variant="outlined" component="label">
                                Upload Logo
                                <input type="file" accept="image/*" hidden onChange={handleLogoUpload} />
                            </Button>

                            {logoData && <Typography>Logo Attached</Typography>}
                        </Stack>
                    </Paper>


                </Grid>

                {/* Preview Column */}
                <Grid item lg={5} xs={12} md={6}>
                    <Box elevation={3}
                        sx={{


                            position: 'relative',
                            padding: '30px 0px',
                            textAlign: 'center',
                            // overflow: 'scroll',
                            alignContent: 'center',

                            mx: {
                                xs: 'auto'
                            }

                        }}>
                        <Typography variant="h5" mb={2}>
                            Preview
                        </Typography>

                        {isQrRendering && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(255,255,255,0.65)',
                                    zIndex: 2,
                                }}
                            >
                                <Stack spacing={1} alignItems="center">
                                    <CircularProgress size={28} />
                                    <Typography variant="caption" color="text.secondary">
                                        Updating QR preview…
                                    </Typography>
                                </Stack>
                            </Box>
                        )}

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',  // horizontal centering
                                alignItems: 'center',      // vertical centering
                                // minHeight: '100vh',        // or whatever container height

                                marginBottom: {
                                    xs: '-100px',
                                    sm: '1px'
                                },


                                /* ↓ responsive scaling ↓ */
                                transform: {
                                    xs: 'scale(0.6)',   // phones
                                    sm: 'scale(0.8)',   // tablets / small desktops
                                    md: 'scale(1)',     // medium and up
                                },
                                transformOrigin: 'top center', // adjust pivot as needed
                            }}
                        >

                            <Box
                                ref={previewRef}
                                sx={{
                                    border: `${borderWidth}px solid ${borderColor}`,
                                    borderRadius: 2,
                                    p: `${marginV}px ${marginH}px`,
                                    display: 'inline-block',
                                    backgroundColor: bgColor,

                                    /* ↓ responsive scaling ↓ */
                                    // transform: {
                                    //     xs: 'scale(0.6)',   // phones
                                    //     sm: 'scale(0.8)',   // tablets / small desktops
                                    //     md: 'scale(1)',     // medium and up
                                    // },
                                    // transformOrigin: 'top center', // adjust pivot as needed
                                }}
                            >
                                {showScanMe && scanMePosition === 'top' && (
                                    <Box sx={{
                                        bgcolor: scanMeBorderColor,
                                        py: 1,
                                        border: `1px solid ${scanMeBorderColor}`
                                    }}>
                                        <Typography sx={{
                                            color: scanMeFontColor,
                                            fontWeight: 'bold',
                                            fontSize: `${scanMeFontSize}px`
                                        }}>
                                            {ScanMe}
                                        </Typography>
                                    </Box>
                                )}
                                <Box sx={{ position: 'relative', width: 300, height: 300, mx: 'auto' }}>
                                    <Box ref={qrCodeRef} />
                                    {logoData && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                width: '40%',
                                                maxWidth: '80%',
                                                transform: 'translate(-50%, -50%)',
                                                p: `${marginH}px`,
                                                borderRadius: 1.5,
                                                bgcolor: bgColor,
                                                boxSizing: 'content-box',
                                                pointerEvents: 'none',
                                                lineHeight: 0,
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={logoData}
                                                alt="QR logo"
                                                sx={{ width: '100%', height: 'auto', display: 'block' }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                                {showScanMe && scanMePosition === 'bottom' && (
                                    <Box sx={{
                                        bgcolor: scanMeBorderColor,
                                        py: 1,
                                        border: `1px solid ${scanMeBorderColor}`
                                    }}>
                                        <Typography sx={{
                                            color: scanMeFontColor,
                                            fontWeight: 'bold',
                                            fontSize: `${scanMeFontSize}px`
                                        }}>
                                            {ScanMe}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        <Box sx={{
                            margin: {
                                sm: '20px',
                            },
                        }}>

                            <Typography>Download as</Typography>
                            <Box className="download-button-grid" aria-label="Download QR code formats" sx={{ mt: 2.5 }}>
                                {['png', 'jpeg', 'svg', 'pdf'].map((ext) => (
                                    <Button
                                        key={ext}
                                        variant="contained"
                                        className={`download-format-btn download-format-btn--${ext}`}
                                        onClick={() => handleDownload(ext)}
                                    >
                                        <Box className="download-format-icon" aria-hidden="true">
                                            <DownloadIcon />
                                        </Box>
                                        <Box component="span" className="download-format-version">
                                            {ext.toUpperCase()}
                                        </Box>
                                    </Button>
                                ))}
                            </Box>

                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                sx={{ mt: 2, background: "linear-gradient(45deg, #4a90e2 0%, #9013fe 100%)", }}
                                onClick={handleSaveSettings}
                            >
                                Save QR Settings
                            </Button>

                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Container >
    );
};

export default DesignerTemplatesPanel;